import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sendConsent } from '../api';
import { giveConsent } from '../consent';
import { useSession } from '../session';
import { Icon } from '../material';
import { Button, ErrorNote } from '../widgets';
import { REMOVE_ACCOUNT_PATH, RemoveAccountSheet, useCloseRemoval } from './remove-account';

/**
 * «Согласие» — consent to the passenger's data being processed, asked of an account
 * the box at «Регистрация» did not reach: one from before it, or one that ticked a text
 * replaced since. A port of `consent_screen.dart`; `Root` decides when, and never over
 * a ride.
 *
 * The box from «Регистрация», unticked, with the text of the consent under it, and
 * «Продолжить» waiting for it. Ticking is a record (ADR 0030): the screen goes at once
 * and the consent follows to the server, with when it was ticked. Somebody who does
 * not agree is told what that means, with «Удалить аккаунт», the same sheet as in
 * «Профиль» (ADR 0048), and «Выйти из аккаунта».
 */
export function ConsentScreen() {
  const { api, me, setMe, refreshMe, signOut } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const closeRemoval = useCloseRemoval('/');
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const again = me?.consentEdition != null;

  const go = async () => {
    const due = me?.consentDue;
    if (!me || !due) return;
    setBusy(true);
    setError(null);
    const given = await giveConsent(me.id, due, (consent) => sendConsent(api, consent));
    if (given.kind !== 'refused') {
      // Taken or kept, the passenger is through; the profile stops saying it is due.
      setMe({ ...me, consentDue: null });
      if (given.kind === 'sent') void refreshMe().catch(() => {});
      return;
    }
    setBusy(false);
    setTicked(false);
    setError(given.message);
    // A text replaced meanwhile: the profile names the new one to tick.
    if (given.code === 'consent_outdated') void refreshMe().catch(() => {});
  };

  return (
    <div className="foot-page">
      <div className="column auth">
        <div>
          <div className="auth-brand">
            <div className="label">Лимузин</div>
            <h1>Согласие на обработку данных</h1>
            <p>
              {again
                ? 'Текст согласия на обработку персональных данных изменился. Прочитайте его и подтвердите, чтобы пользоваться Лимузином дальше.'
                : 'Теперь мы отдельно спрашиваем согласие на обработку персональных данных. Прочитайте его и подтвердите, чтобы пользоваться Лимузином дальше.'}
            </p>
          </div>

          <div className="stack" style={{ marginTop: 16, gap: 10 }}>
            <button
              type="button"
              className="remember"
              role="checkbox"
              aria-checked={ticked}
              disabled={busy}
              onClick={() => setTicked(!ticked)}
            >
              <span className={ticked ? 'checkbox on' : 'checkbox'}>
                {ticked && <Icon name="check_rounded" size={13} />}
              </span>
              Даю согласие на обработку персональных данных
            </button>
            <p className="caption legal-note">
              <a href="/legal/consent-passenger.html" target="_blank" rel="noopener">
                Текст согласия
              </a>
            </p>
          </div>

          <ErrorNote error={error} />
        </div>

        <div className="foot-spacer" />
        <Button busy={busy} disabled={!ticked || busy} onClick={() => void go()}>
          Продолжить
        </Button>
        <p className="caption legal-note" style={{ textAlign: 'center', margin: '14px 4px 0' }}>
          Без согласия пользоваться Лимузином нельзя. Не согласны —{' '}
          <a
            href={`#${REMOVE_ACCOUNT_PATH}`}
            onClick={(event) => {
              event.preventDefault();
              navigate(REMOVE_ACCOUNT_PATH);
            }}
          >
            удалите аккаунт
          </a>
          .
        </p>
        <p style={{ textAlign: 'center', margin: '6px 0 0' }}>
          <button type="button" className="linkish" disabled={busy} onClick={signOut}>
            Выйти из аккаунта
          </button>
        </p>
      </div>
      {pathname === REMOVE_ACCOUNT_PATH && (
        <RemoveAccountSheet onClose={closeRemoval} />
      )}
    </div>
  );
}
